import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MeetingDto,
  MeetingTranscriptSegmentDto,
} from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import {
  formatarMarcaDeTempo,
  mensagemDoErro,
} from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Transcrição de uma ligação, trecho a trecho: falante, tempo (mm:ss) e texto (card 3112).
 *
 * Mesmas duas portas da gravação: com `dealId` pela aba do deal, sem ele pela visão geral.
 */
@Component({
  selector: 'app-modal-meeting-transcript',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './modal-meeting-transcript.component.html',
  styleUrl: './modal-meeting-transcript.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetingTranscriptComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  protected readonly ligacao = signal<MeetingDto | null>(null);
  protected readonly trechos = signal<MeetingTranscriptSegmentDto[]>([]);
  protected readonly carregando = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly tempo = formatarMarcaDeTempo;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService
  ) {
    super(translateService);
  }

  open(meeting: MeetingDto, dealId: string | null = null): void {
    this.ligacao.set(meeting);
    this.trechos.set([]);
    this.erro.set(null);
    this.carregando.set(true);

    this.dialogRef?.open('', '', '', 'Fechar', '', 'meeting-transcript', false, true);

    const pedido = dealId
      ? this.meetingsService.getDealMeetingTranscript(dealId, meeting.id)
      : this.meetingsService.getTranscript(meeting.id);

    pedido.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: resposta => {
        // Resposta atrasada de uma ligação aberta antes: não troca a transcrição da que está na tela.
        if (this.ligacao()?.id !== meeting.id) return;
        this.carregando.set(false);
        this.trechos.set(resposta?.segments ?? []);
      },
      error: erro => {
        if (this.ligacao()?.id !== meeting.id) return;
        this.carregando.set(false);
        this.erro.set(mensagemDoErro(erro, 'Não foi possível abrir a transcrição.'));
      },
    });
  }

  /** Só leitura: não há formulário. Existe para seguir o contrato das modais do projeto. */
  isFormValid(): boolean {
    return true;
  }

  submitAction(): void {
    this.fechar();
  }

  fechar(): void {
    this.dialogRef?.close();
  }

  /** Falante repetido em sequência não repete o nome — lê como conversa, não como planilha. */
  protected mesmoFalanteQueOAnterior(indice: number): boolean {
    if (indice === 0) return false;
    const lista = this.trechos();
    return (lista[indice].speaker ?? '') === (lista[indice - 1].speaker ?? '');
  }
}
